<template>
  <div>
    <TitleBanner title="Who are you representing today" />
    <div class="my-5 space-y-5 p-3">
      <BaseInput
        v-model:input-value="companyName"
        :is-valid="errorMessage.companyName === ''"
        :required="true"
        identifier="visitor-access-representation-company"
        input-mode="text"
        label="Company Name"
        @focused="clearCompanyName"
      >
        <div
          v-show="showCompanyNameList && visitorCompanyNames.length !== 0"
          class="visitorCompanyNameList absolute z-20 w-full bg-white"
        >
          <span
            v-for="name in visitorCompanyNames"
            :key="visitorCompanyNames.indexOf(name)"
            class="block border px-2 py-3"
            @click="companySelected"
          >
            {{ name }}
          </span>
        </div>
      </BaseInput>
      <span v-if="errorMessage.companyName" class="error m-1 !my-0 block">
        {{ errorMessage.companyName }}
      </span>
      <BaseSelector
        :label-classes="
          selectedUserType.id > 0
            ? ['text-blue-400 absolute left-2 text-xs top-1']
            : ['text-gray-600 absolute left-2.5 top-3']
        "
        :options="userTypeList"
        :select-element-style="`block border-gray-300 focus:ring-blue-500 focus:border-blue-500 focus:outline-none py-3 rounded-md w-full text-gray-600 ${
          selectedUserType.id > 0 ? 'pt-5' : ''
        }`"
        :should-auto-select="true"
        data-test="visitor-type-list"
        label="I am a ..."
        @option-changed="userTypeSelected"
      ></BaseSelector>
      <span v-if="errorMessage.userType" class="error m-1 !my-0 block">
        {{ errorMessage.userType }}
      </span>
      <PrimaryButton
        :key="continueButton"
        action="Continue"
        identifier="visitor-access-representation-continue"
        type="submit"
        @click.once="continueButtonClicked"
      />
    </div>
    <LoadingView :is-loading="isLoading" message="Setting selections..." />
  </div>
</template>

<script lang="ts">
  import { defineComponent, reactive, ref, watch } from 'vue'
  import PrimaryButton from '@/components/PrimaryButton.vue'
  import BaseInput from '@/components/BaseInput.vue'
  import BaseSelector from '@/components/BaseSelector.vue'
  import TitleBanner from '@/components/TitleBanner.vue'
  import {
    getCustomerVisitorUserTypes,
    UserType,
  } from '@/services/api/customer'
  import { onBeforeRouteLeave, useRouter } from 'vue-router'
  import { visitorCheckin } from '@/services/api/siteSelected'
  import LoadingView from '@/views/LoadingView.vue'
  import InvalidStoreException from '@/exceptions/InvalidStoreException'
  import { resetDataStores } from '@/components/use/resetStore'
  import { useUserLogin } from '@/storage/userLogin'
  import { useCustomerStore } from '@/storage/customer'
  import { storeToRefs } from 'pinia'
  import { useUserPositionStore } from '@/storage/userPosition'
  import { useSessionStore } from '@/storage/session'
  import { useSiteStore } from '@/storage/site'
  import { useUserVisitorCompaniesStore } from '@/storage/userVisitorCompanies'
  import { useUserStore } from '@/storage/user'
  import { getSite } from '@/services/api/site'
  import { LocationRequirementStatus } from '@/types/locationRequirementStatuses'

  type FormFieldName = 'companyName' | 'userType'

  export default defineComponent({
    name: 'VisitorAccessRepresentation',

    components: {
      BaseInput,
      BaseSelector,
      LoadingView,
      PrimaryButton,
      TitleBanner,
    },

    async setup() {
      const { setUserType, setCheckedInTime } = useUserStore()
      const { setUserLoginIsVisitor } = useUserLogin()
      const { setLastUsedCompany, setVisitorCompanyName } =
        useUserVisitorCompaniesStore()
      const { names, lastSelected } = storeToRefs(
        useUserVisitorCompaniesStore(),
      )
      const router = useRouter()
      const {
        uuid: siteUuid,
        id: siteId,
        isSiteStoreDefault,
      } = storeToRefs(useSiteStore())
      const { position } = storeToRefs(useUserPositionStore())
      const { checkLocationRequirements } = useUserPositionStore()
      const { id: customerId, isCustomerStoreDefault } =
        storeToRefs(useCustomerStore())
      const { setSiteSessionId, setSessionTtl } = useSessionStore()

      const companyName = ref(lastSelected.value)
      const visitorCompanyNames = ref<string[]>(names.value)
      const selectedUserType = ref<UserType>({ id: 0, name: '' })
      const continueButton = ref(0)
      const userTypeList = ref<UserType[] | []>([])
      const showCompanyNameList = ref(false)
      const isLoading = ref(false)
      const errorMessage = reactive({
        companyName: '',
        userType: '',
      })

      onBeforeRouteLeave((to) => {
        if (to.name === 'checkWorkOrder') {
          resetDataStores()
        }
      })

      watch(companyName, (newValue) => {
        showErrorOnEmptyField('companyName', companyName.value)
        if (!visitorCompanyNames.value.includes(newValue)) {
          showCompanyNameList.value = true
        }
        companyNameChanged(newValue)
      })

      watch(selectedUserType, () =>
        showErrorOnEmptyField('userType', selectedUserType.value.id),
      )

      await fetchUserType()

      async function fetchUserType() {
        if (isCustomerStoreDefault.value) {
          throw new InvalidStoreException()
        }
        userTypeList.value = await getCustomerVisitorUserTypes(customerId.value)
      }

      async function continueButtonClicked() {
        showErrorOnEmptyField('companyName', companyName.value)
        showErrorOnEmptyField('userType', selectedUserType.value.id)

        if (!canContinue()) {
          continueButton.value += 1
          return
        }

        isLoading.value = true
        setUserType(selectedUserType.value)
        setLastUsedCompany(companyName.value)
        setVisitorCompanyName(companyName.value)
        setUserLoginIsVisitor(true)

        if (isSiteStoreDefault.value) {
          throw new InvalidStoreException()
        }

        const locationCheck = await checkLocationRequirements()
        if (
          [
            LocationRequirementStatus.PositionError,
            LocationRequirementStatus.NotPrecise,
          ].includes(locationCheck)
        ) {
          await router.replace({ name: 'helpLocationPermission' })
          return
        }

        if (locationCheck === LocationRequirementStatus.NotOnSite) {
          await router.replace({ name: 'notOnSite' })
          return
        }

        const response = await visitorCheckin(
          siteId.value,
          companyName.value,
          selectedUserType.value.id,
          position.value,
        )

        if (response.code === 200) {
          setCheckedInTime(response.checkedInAt)
          setSessionTtl(response.sessionTtl)
          setSiteSessionId(response.siteSessionId)
          const checkedInSite = await getSite(siteUuid.value)
          useSiteStore().setSiteDocuments(checkedInSite.siteDocuments)
          useSiteStore().setSiteHazards(checkedInSite.siteHazards)
          useCustomerStore().setCustomerDocuments(
            checkedInSite.customer.customerDocuments,
          )
          await router.replace({ name: 'checkWorkOrder' })
        }

        isLoading.value = false
      }

      function canContinue() {
        return errorMessage.companyName === '' && errorMessage.userType === ''
      }

      function userTypeSelected(userTypeId: number) {
        selectedUserType.value = (userTypeList.value as UserType[]).find(
          (userType) => userType.id === userTypeId,
        ) ?? { id: 0, name: '' }
      }

      function companySelected(event) {
        companyName.value = event.target.innerText
        showCompanyNameList.value = false
      }

      function showErrorOnEmptyField(
        fieldName: FormFieldName,
        fieldValue: string | number | undefined,
      ) {
        if (fieldValue) {
          errorMessage[fieldName] = ''
        } else {
          errorMessage[fieldName] = 'Field cannot be empty'
        }
      }

      function companyNameChanged(change: string) {
        visitorCompanyNames.value = names.value.filter((name: string) =>
          name.toLowerCase().startsWith(change.toLowerCase()),
        )
      }

      function clearCompanyName() {
        companyName.value = ''
      }

      return {
        clearCompanyName,
        companyName,
        companySelected,
        continueButton,
        continueButtonClicked,
        errorMessage,
        isLoading,
        selectedUserType,
        showCompanyNameList,
        userTypeList,
        userTypeSelected,
        visitorCompanyNames,
      }
    },
  })
</script>
